/**
 * Pure helpers for the Members Add / Edit dialogs' Workspace + Department
 * picker state. Kept out of the page component so the rules can be unit
 * tested without React or a DOM.
 */

/**
 * The Department id to SAVE for each ticked Workspace, keyed by workspace id.
 *
 * A company-wide Work Role transcends Departments, so when one is selected
 * every Workspace saves "— No Department —" (empty string).
 *
 * This is derived AT SAVE TIME on purpose. An earlier version cleared
 * `departmentId` inside a `useEffect` the moment the selected Work Role
 * became company-wide. That destroyed the member's real Department as soon as
 * an admin merely previewed such a role, and nothing put it back when they
 * switched away again — so the save diff read as "Department removed" and
 * silently stripped it server-side. Deriving it here instead means switching
 * to a company-wide role and back is a no-op.
 *
 * @param {Array<{workspaceId: string, departmentId?: string|null}>} assignments
 * @param {{companyWide?: boolean}} [opts]
 * @returns {Map<string, string>} workspaceId → departmentId ('' = none)
 */
export function departmentsToSave(assignments = [], { companyWide = false } = {}) {
  return new Map(
    assignments.map((a) => [
      a.workspaceId,
      companyWide ? '' : a.departmentId || '',
    ]),
  )
}

/**
 * Validate the Workspace + Department picker. Returns '' when valid, or the
 * message to show under the field.
 *
 * The rule: a Department-scoped or Self-scoped Work Role MUST belong to a
 * Department. Only a company-wide Work Role may go without one, because it
 * transcends Departments — and its Department selects are disabled, so
 * demanding one there would be an unsatisfiable gate the admin cannot clear.
 *
 * Callers must not enforce this before the Work Role is resolvable (the
 * catalogue still loading, or no role picked yet) — otherwise the dialog
 * flags a field the admin hasn't reached.
 *
 * @param {Array<{workspaceId: string, departmentId?: string|null}>} assignments
 * @param {{hasMultipleWorkspaces?: boolean, soleWorkspaceId?: string, companyWide?: boolean}} [opts]
 * @returns {string} '' when valid
 */
export function departmentAssignmentError(
  assignments = [],
  { hasMultipleWorkspaces = false, soleWorkspaceId = '', companyWide = false } = {},
) {
  // Multi-Workspace tenants always need at least one Workspace ticked. With a
  // single Workspace the member is attached to it implicitly, so an empty
  // list just means the seeding effect hasn't resolved yet — nothing to flag.
  if (assignments.length === 0) {
    return hasMultipleWorkspaces ? 'Please select at least one Workspace.' : ''
  }
  // Company-wide: Departments do not apply in ANY Workspace.
  if (companyWide) return ''

  if (hasMultipleWorkspaces) {
    return assignments.some((a) => !a.departmentId)
      ? 'Please pick a Department for every selected Workspace.'
      : ''
  }
  const dept = assignments.find((a) => a.workspaceId === soleWorkspaceId)?.departmentId
  return dept ? '' : 'Please select a Department.'
}
